import { DateTime } from '@ksyos/date-time';
import { Button, Card, FormGroup } from '@ksyos/design-system';
import { uniqBy } from 'lodash';
import { useEffect, useState } from 'react';
import type { AgbCode } from '@common/interfaces/agbCode';
import type { IOrganization } from '@frontend/components/interfaces/organization';
import type { IOrganizationGetOne } from '@frontend/components/interfaces/organizationGetOne';
import type { ValidationErrors } from '@frontend/utils/validation';
import { OrganizationRelationshipType, OrganizationType } from '@common/enums';
import { OrganizationStructureType } from '@common/enums/organizationStructureType';
import { isDuplicateDataError, type OrganizationDataError } from '@common/interfaces/api/organizationDataErrorsApi';
import { emptyUuid } from '@common/utils/uuid';
import { isAgbCodeRequiredForOrganizationProps } from '@common/validation/organization';
import { EnumSelect } from '@frontend/components/fields/EnumSelect';
import { renderTextInput } from '@frontend/components/pages/organizations/organizationAdd/utils/RenderTextInput';
import { InputTextField } from '@frontend/components/userDataManagement/InputText';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortableFetch } from '@frontend/utils';
import type { OrganizationRelationshipAction } from '../utils/organizationRelationshipsReducer';
import { OrganizationRelationshipFormComponent } from '../OrganizationRelationshipFormComponent';
import { OrganizationRelationshipsActions } from '../utils/organizationRelationshipsReducer';
import { DuplicateOrganizationsAlert } from './DuplicateOrganizationsAlert';

type GeneralInformationCardProps = {
    dataErrors: OrganizationDataError[];
    dispatchRelationshipAction: React.Dispatch<OrganizationRelationshipAction<IOrganization>>;
    errors: ValidationErrors;
    handleChangeAndValidate: <K extends keyof IOrganization>(name: K, value: IOrganization[K]) => void;
    organization: IOrganization;
    setErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>;
};

export function GeneralInformationCard({
    dataErrors,
    dispatchRelationshipAction,
    errors,
    handleChangeAndValidate,
    organization,
    setErrors,
}: Readonly<GeneralInformationCardProps>) {
    const abortableFetch = useAbortableFetch();
    const [duplicateOrganizations, setDuplicateOrganizations] = useState<IOrganizationGetOne[]>([]);

    const translate = getSelectedLanguage();
    const isAgbCodeRequired = isAgbCodeRequiredForOrganizationProps({
        organizationStructureType: organization.organizationStructureType,
        organizationType: organization.organizationType,
    });

    useEffect(() => {
        updateDuplicateOrganizations();
    }, [dataErrors]);

    return (
        <Card>
            <Card.Header title={translate.organization.generalInformation} />
            <Card.Body>
                <DuplicateOrganizationsAlert duplicateOrganizations={duplicateOrganizations} />

                <FormGroup
                    hasErrors={errors.has('organizationStructureType')}
                    isRequired
                    label={translate.organization.organizationStructureType}
                >
                    <EnumSelect
                        enumObj={OrganizationStructureType}
                        isClearable={false}
                        isMulti={false}
                        labels={translate.organizationStructureTypeEnum}
                        onChange={(item) => {
                            if (item?.value) {
                                handleChangeAndValidate('organizationStructureType', item.value);
                            }
                        }}
                        value={organization.organizationStructureType}
                    />
                </FormGroup>

                {renderTextInput({
                    errors,
                    fieldKey: 'name',
                    isRequired: true,
                    label: 'organization.name',
                    onDoValidate: updateDuplicateOrganizations,
                })}

                {renderTextInput({
                    errors,
                    fieldKey: 'nameStatutory',
                    isRequired: false,
                    label: 'organization.nameStatutory',
                })}

                {renderTextInput({
                    errors,
                    fieldKey: 'alias',
                    isRequired: false,
                    label: 'organization.alias',
                })}

                {renderTextInput({
                    errors,
                    fieldKey: 'kvkNumber',
                    isRequired: false,
                    label: 'organization.kvkNumber',
                    maxLength: 8,
                })}

                <FormGroup
                    hasErrors={errors.has('organizationType')}
                    isRequired
                    label={translate.organization.organizationType}
                >
                    <EnumSelect
                        enumObj={OrganizationType}
                        isClearable={false}
                        isMulti={false}
                        labels={translate.organizationTypeEnum}
                        onChange={(item) => {
                            if (item?.value) {
                                handleChangeAndValidate('organizationType', item.value);
                            }
                        }}
                        value={organization.organizationType}
                    />
                </FormGroup>

                <InputTextField
                    error={findAgbCodeError()}
                    hasErrors={hasAgbCodeError()}
                    isRequired={isAgbCodeRequired}
                    label={'organization.agbCode'}
                    nullable={!isAgbCodeRequired}
                    onChange={handleAgbCodeChange}
                    value={organization.agbCodes?.[0]?.agbCode ?? null}
                />

                <FormGroup
                    hasErrors={hasOrganizationRelationshipsError()}
                    isRequired={organization.organizationRelationships.length >= 1}
                    label={translate.organization.organizationRelationships}
                >
                    {organization.organizationRelationships.map((organizationRelationship, index) => (
                        <OrganizationRelationshipFormComponent
                            dispatchRelationshipAction={dispatchRelationshipAction}
                            index={index}
                            key={index}
                            onRemoveValidationErrors={handleRemoveValidationErrors}
                            organization={organization}
                            organizationRelationship={organizationRelationship}
                            relationshipsErrors={errors}
                        />
                    ))}
                </FormGroup>

                <Button
                    iconLeft="add"
                    onClick={() => {
                        dispatchRelationshipAction({
                            payload: {
                                relatedOrganizationId: emptyUuid,
                                type: OrganizationRelationshipType.isBranchOf,
                            },
                            type: OrganizationRelationshipsActions.addRelationship,
                        });
                    }}
                    variant="link"
                >
                    {translate.organization.addOrganizationRelationship}
                </Button>
            </Card.Body>
        </Card>
    );

    function handleRemoveValidationErrors(keys: string[]): void {
        setErrors((prevErrors: ValidationErrors) => keys.reduce((acc, key) => acc.remove(key), prevErrors));
    }

    function hasOrganizationRelationshipsError() {
        return errors.keySeq().some((key) => key?.startsWith('organizationRelationships') === true);
    }

    function hasAgbCodeError() {
        return errors.keySeq().some((key) => key?.startsWith('agbCodes') === true);
    }

    function findAgbCodeError() {
        const key = errors.keySeq().find((key) => key?.startsWith('agbCodes') === true);

        return key ? errors.get(key) : undefined;
    }

    async function updateDuplicateOrganizations() {
        const duplicatesByDataErrors = (
            await Promise.all(
                uniqBy(
                    dataErrors.filter(isDuplicateDataError).flatMap((error) => error.details.duplicateOrganizations),
                    (duplicateOrganization) => duplicateOrganization.id
                ).map((duplicateOrganization) =>
                    abortableFetch.getJson(`/api/organizations/${duplicateOrganization.id}`)
                )
            )
        ).map((result) => result.json as IOrganizationGetOne);

        const duplicatesByName = await findDuplicateOrganizationsByName();

        const duplicates = uniqBy(
            duplicatesByDataErrors.concat(duplicatesByName),
            (duplicateOrganization) => duplicateOrganization.id
        ).sort((a, b) => a.name.localeCompare(b.name));

        setDuplicateOrganizations(duplicates);
    }

    async function findDuplicateOrganizationsByName(): Promise<IOrganizationGetOne[]> {
        if (!organization.name) {
            return [];
        }

        const url = '/api/organizations/list';
        const result = await abortableFetch.fetchJson(url, 'POST', { filters: { name: organization.name } });

        const { items } = result.json;

        return items;
    }

    function handleAgbCodeChange(value: string | null): void {
        const agbCodes: AgbCode[] | undefined = value
            ? [{ agbCode: value, endDate: null, startDate: DateTime.today().format('YYYY-MM-DD') }]
            : undefined;

        dispatchRelationshipAction({
            payload: {
                key: 'agbCodes',
                value: agbCodes,
            },
            type: OrganizationRelationshipsActions.updateOrganizationKeyValue,
        });
    }
}
